import { solicitudActividadService } from './solicitudActividadService';
import { supabase, ActividadEconomica } from '../lib/supabase';
import { OwnerContext, buildOwnerPayload } from '../types/actividad';

export const actividadService = {
  async getByLumisial(lumisialId: string): Promise<ActividadEconomica[]> {
    const { data, error } = await supabase
      .from('actividades_economicas')
      .select('*')
      .eq('lumisial_id', lumisialId)
      .order('fecha_inicio', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<ActividadEconomica | null> {
    const { data, error } = await supabase
      .from('actividades_economicas')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  async getActiveCountByLumisial(lumisialId: string): Promise<number> {
    const { count, error } = await supabase
      .from('actividades_economicas')
      .select('*', { count: 'exact', head: true })
      .eq('lumisial_id', lumisialId)
      .in('estado', ['planificacion', 'en_curso']);

    if (error) throw error;
    return count || 0;
  },

  async getActiveByLumisial(lumisialId: string): Promise<ActividadEconomica[]> {
    const { data, error } = await supabase
      .from('actividades_economicas')
      .select('*')
      .eq('lumisial_id', lumisialId)
      .in('estado', ['planificacion', 'en_curso'])
      .order('fecha_inicio', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async getByDiocesis(diocesisId: string): Promise<ActividadEconomica[]> {
    const { data, error } = await supabase
      .from('actividades_economicas')
      .select('*')
      .eq('diocesis_id', diocesisId)
      .is('lumisial_id', null)
      .order('fecha_inicio', { ascending: false });
    if (error) throw error;
    return data ?? [];
  },

  async getByProvincia(provinciaId: string): Promise<ActividadEconomica[]> {
    const { data, error } = await supabase
      .from('actividades_economicas')
      .select('*')
      .eq('provincia_eclesiastica_id', provinciaId)
      .is('lumisial_id', null)
      .order('fecha_inicio', { ascending: false });
    if (error) throw error;
    return data ?? [];
  },

  async getPaginated(
    lumisialId: string,
    page: number = 1,
    pageSize: number = 20,
    estado?: 'planificacion' | 'en_curso' | 'finalizada' | 'archivada',
    searchTerm?: string,
    incluirArchivadas: boolean = false
  ): Promise<{ data: ActividadEconomica[]; total: number; page: number; pageSize: number; totalPages: number }> {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('actividades_economicas')
      .select('*', { count: 'exact' })
      .eq('lumisial_id', lumisialId);

    if (estado) {
      query = query.eq('estado', estado);
    } else if (!incluirArchivadas) {
      query = query.neq('estado', 'archivada');
    }

    if (searchTerm?.trim()) {
      query = query.or(`nombre.ilike.%${searchTerm}%,descripcion.ilike.%${searchTerm}%`);
    }

    const { data, error, count } = await query
      .order('fecha_inicio', { ascending: false })
      .range(from, to);

    if (error) throw error;

    return {
      data: data || [],
      total: count || 0,
      page,
      pageSize,
      totalPages: Math.ceil((count || 0) / pageSize)
    };
  },

  async create(
    actividad: Omit<ActividadEconomica, 'id' | 'created_at' | 'lumisial_id' | 'diocesis_id' | 'provincia_eclesiastica_id'>,
    ownerContext: OwnerContext
  ): Promise<ActividadEconomica> {
    // Convertir strings vacías a null para campos de fecha
    const actividadLimpia = {
      ...actividad,
      ...buildOwnerPayload(ownerContext),
      fecha_inicio: actividad.fecha_inicio || null,
      fecha_fin: actividad.fecha_fin || null,
      descripcion: actividad.descripcion || null
    };

    const { data, error } = await supabase
      .from('actividades_economicas')
      .insert(actividadLimpia)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, actividad: Partial<Omit<ActividadEconomica, 'id' | 'created_at'>>): Promise<ActividadEconomica> {
    const { data, error } = await supabase
      .from('actividades_economicas')
      .update(actividad)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return data;
  },

  async updateEstado(id: string, estado: 'planificacion' | 'en_curso' | 'finalizada'): Promise<ActividadEconomica> {
    const { data, error } = await supabase
      .from('actividades_economicas')
      .update({ estado })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async archivar(id: string): Promise<ActividadEconomica> {
    const { data, error } = await supabase
      .from('actividades_economicas')
      .update({ estado: 'archivada' })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const datos = { fecha_anulacion: new Date().toISOString() };
    await solicitudActividadService.ejecutarContrato('eliminar_actividad',
      { p_id: id, p_datos: datos }, { id, datos });
  }
};
